// ============================================================================
// TYPED NODE CONFIGS
// ============================================================================

export interface TimeWindow {
  start: string; // "HH:MM"
  end: string;   // "HH:MM"
}

export interface GroupFilters {
  min_members: number;
  activity_level: 'any' | 'active' | 'very_active';
}

// Account Selector Config
export interface AccountSelectorConfig {
  platform: 'facebook' | 'instagram' | 'threads' | 'tiktok' | 'zalo' | 'linkedin';
  selected_accounts: string[];
  selected_fanpages?: string[];
  mode: 'all' | 'specific';
  masterTimeline?: MasterTimeline;
  account_fanpage_map?: Record<string, string>; // account_id → fanpage_url (FB only, optional)
}

// Brand Info Config - NHÃNH HIỆU ĐỂ SEEDING
export interface BrandInfoConfig {
  brand_name: string;
  industry: string;
  category: string;
  product_names: string[];
  website?: string;
  description: string;
  core_topics: string[];
  target_demographics: {
    age_groups: string[];
    locations: string[];
    interests: string[];
  };
  mention_style: 'organic' | 'testimonial' | 'educational' | 'promotional';
  tone_of_voice: string;
}

// Seeding Contents Manager Config
// Controls which posts should be seeded by which accounts and how frequently.
export interface FrequencyControl {
  max_total_per_post: number;    // Maximum total interactions per post URL (0 = unlimited)
  max_per_day_per_post: number;  // Max interactions per day per post URL (0 = unlimited)
  cooldown_hours: number;        // Hours to wait before re-interacting with the same post
}

export interface SeedingPostEntry {
  account_id: string;                    // Seeding account ID
  source_kind?: 'account' | 'fanpage';   // follow source type
  post_mode: 'specific' | 'follow';      // specific = manual URLs; follow = auto from seeding_contents DB
  post_urls: string[];                   // Only used when post_mode = 'specific'
  selected_folder_ids?: string[];        // Only used when post_mode = 'follow'; empty = all folders
  selected_fanpage_page_ids?: string[];  // follow fanpage source filter by page_id; empty = all fanpages
  frequency_control: FrequencyControl;  // Quota rules enforced via Redis
}

export interface SeedingContentsManagerConfig {
  manager_id?: string;                // Optional reference to persisted seeding_contents_manager document
  name: string;
  seeding_accounts: SeedingPostEntry[];
}

// Reply to Comments Config
export interface ReplyToCommentsConfig {
  post_url: string;              // Used when dispatch_mode = 'specific' (or missing)
  max_replies: number;
  max_comments: number;
  content_hints: string;
  link_url?: string;
  comment_style?: Record<string, any>;
  brand_info_node_id?: string;
  /** Dispatch mode for managed seeding:
   *  - 'specific'      – use post_url field directly (default / backward-compat)
   *  - 'managed'       – pick post URL from the referenced SeedingContentsManager node
   *  - 'group_posts'   – use post URLs collected by the referenced find_group_posts node
   *  - 'filter_posts'  – use post URLs from live results of a filter_post node in the same run
   *  - 'from_top_posts' – use post URLs from live results of a find_top_posts node in the same run */
  dispatch_mode?: 'specific' | 'managed' | 'group_posts' | 'filter_posts' | 'from_top_posts';
  /** ID of the seeding_contents_manager workflow node to resolve URLs from (managed mode) */
  seeding_contents_manager_node_id?: string;
  /** ID of the find_group_posts workflow node to read post URLs from (group_posts mode) */
  find_group_posts_node_id?: string;
  /** ID of the filter_post workflow node to read live post results from (filter_posts mode) */
  filter_post_node_id?: string;
  /** ID of the find_top_posts workflow node to read live post results from (from_top_posts mode) */
  find_top_posts_node_id?: string;
  /** Reply mode: 'random' picks one random comment per call; 'keyword' replies to all matching-keyword comments */
  reply_mode?: 'random' | 'keyword';
  /** Keywords to match when reply_mode = 'keyword' (case-insensitive substring match) */
  reply_keywords?: string[];
  /** Max replies per keyword (0 = all matching). Only used in keyword mode. */
  reply_max_per_keyword?: number;
}

// Post to Profile Config
export interface PostToProfileConfig {
  content_type: 'text' | 'image' | 'both';
  /** @deprecated Use image_source_type instead */
  has_image: boolean;
  content_hints: string;
  link_url?: string;
  brand_info_node_id?: string;
  /** Image source for post attachment:
   *  - "none"        – no image (default / backward-compat)
   *  - "context"     – vault_asset_id produced by a previous node (ai_generate_asset)
   *  - "vault_asset" – static pick from vault library (requires image_asset_id)
   *  - "vault_folder"– sequential/random pick from a vault folder (requires vault_folder_id) */
  image_source_type?: 'none' | 'context' | 'vault_asset' | 'vault_folder' | 'character_context';
  /** MongoDB ObjectID hex of MediaAsset — required when image_source_type = 'vault_asset' */
  image_asset_id?: string;
  /** Vault folder ID — required when image_source_type = 'vault_folder' */
  vault_folder_id?: string;
  /** If true, worker registers the resulting post URL into seeding_contents collection
   *  so it can be picked up by Comment/Reply nodes in 'managed' dispatch mode. */
  add_to_seeding_contents?: boolean;
  /** Folder IDs to register seeding content into; empty array = default folder. */
  seeding_folder_ids?: string[];
  /** Brand character ID for persona/voice/vault link */
  character_id?: string;
}

// Post to Group Config
export interface PostToGroupConfig {
  group_urls: string[];
  content_hints: string;
  link_url?: string;
  brand_info_node_id?: string;
  /** @deprecated Use image_source_type instead */
  has_image: boolean;
  image_source_type?: 'none' | 'context' | 'vault_asset' | 'vault_folder' | 'character_context';
  image_asset_id?: string;
  vault_folder_id?: string;
  /** If true, worker registers the resulting post URL into seeding_contents collection. */
  add_to_seeding_contents?: boolean;
  /** Folder IDs to register seeding content into; empty array = default folder. */
  seeding_folder_ids?: string[];
  /**
   * Chế độ post:
   * - "discussion" (default): bàn luận tự nhiên, theo chủ đề group / upstream context
   * - "seeding": seeding B2C, không theo chủ đề group, content_hints bắt buộc
   */
  post_mode?: 'discussion' | 'seeding';
  /** Khi true, worker sẽ click nút "Bài viết ẩn danh" trên group rồi xác nhận
   * modal trước khi mở compose editor. Luồng post giữ nguyên sau đó. */
  anonymous_post?: boolean;
}

// Vault asset preview for picker UI
export interface VaultAssetPreview {
  id: string;               // MongoDB ObjectID hex
  asset_name: string;       // URL-safe slug
  title: string;            // Display title
  media_type: 'image' | 'video';
  goal: string;             // "branding" | "content" | etc.
  created_at: string;
  /**
   * Tiny (≤128 px) JPEG preview, base64-encoded, generated by wkr-marketing at
   * publish time and stored inline in MongoDB.  Included in ListAssets response.
   * Use as <img src> data URI: `data:image/jpeg;base64,${thumbnail_preview_b64}`.
   * Zero extra HTTP call — preview bytes come directly in the list response.
   * Falls back to full absolute thumbnail URL for legacy assets without this field.
   */
  thumbnail_preview_b64?: string;
}

// Comment on Post Config
export interface CommentOnPostConfig {
  post_url: string;              // Used when dispatch_mode = 'specific' (or missing)
  /** Chế độ comment:
   *  - 'discussion' (default) – comment bàn luận tự nhiên theo Demographic
   *  - 'seeding'              – comment seeding bán hàng B2C */
  comment_mode: 'discussion' | 'seeding';
  /** Seeding intent — chỉ dùng khi comment_mode = 'seeding'. Để trống = random */
  seeding_mode?: 'ask_info' | 'interest' | 'inbox_cta' | 'social_proof' | 'close' | '';
  content_hints: string;
  link_url?: string;
  brand_info_node_id?: string;   // Reference to Brand Info Node (optional)
  /** Dispatch mode for managed seeding:
   *  - 'specific'      – use post_url field directly (default / backward-compat)
   *  - 'managed'       – pick post URL from the referenced SeedingContentsManager node
   *  - 'group_posts'   – use post URLs collected by the referenced find_group_posts node
   *  - 'filter_posts'  – use post URLs from live results of a filter_post node in the same run
   *  - 'from_top_posts' – use post URLs from live results of a find_top_posts node in the same run */
  dispatch_mode?: 'specific' | 'managed' | 'group_posts' | 'filter_posts' | 'from_top_posts';
  /** ID of the seeding_contents_manager workflow node to resolve URLs from (managed mode) */
  seeding_contents_manager_node_id?: string;
  /** ID of the find_group_posts workflow node to read post URLs from (group_posts mode) */
  find_group_posts_node_id?: string;
  /** ID of the filter_post workflow node to read live post results from (filter_posts mode) */
  filter_post_node_id?: string;
  /** ID of the find_top_posts workflow node to read live post results from (from_top_posts mode) */
  find_top_posts_node_id?: string;
  /** Facebook usernames/names to @-tag at the end of the comment (without @) */
  tag_names?: string[];
  /** Hashtags to append at the end of the comment (without #) */
  hashtags?: string[];
}

// Find & Join Groups Config
export interface FindJoinGroupsConfig {
  keywords: string[];
  max_groups_per_week: number;
  time_windows: TimeWindow[];
  filters: GroupFilters;
}

// Join Groups Config — 2 modes
export interface JoinGroupsConfig {
  /**
   * - "random" (default): search by keywords then join matching groups
   * - "by_url": join an explicit list of group URLs provided in group_urls
   */
  join_mode?: 'random' | 'by_url';
  max_joins: number;           // 1–10, default 3
  min_members?: number;        // [random mode] minimum group members (default 0 = no filter)
  keywords?: string[];         // [random mode] search keywords
  group_urls?: string[];       // [by_url mode] explicit Facebook group URLs
}

// Share Post TikTok Config (Đăng lại / Repost)
export interface SharePostTiktokConfig {
  /** Dispatch mode:
   * - 'urls': repost specific video URLs directly
   * - 'seeding_for_account': repost videos from specified TikTok profiles
   * - 'keyword_search': repost videos found via keyword search */
  dispatch_mode: 'urls' | 'seeding_for_account' | 'keyword_search';
  // urls mode
  urls?: string[];
  // seeding_for_account mode
  tiktok_ids?: string[];       // e.g. ["@username1", "@username2"]
  count?: number;              // number of videos to repost per account/keyword
  order?: 'newest' | 'random' | 'top';
  // keyword_search mode
  keyword?: string;
}

// Comment Post TikTok Config
export interface CommentPostTiktokConfig {
  /** Dispatch mode:
   * - 'urls': comment on specific video URLs directly
   * - 'seeding_for_account': comment on videos from specified TikTok profiles
   * - 'keyword_search': comment on videos found via keyword search */
  dispatch_mode: 'urls' | 'seeding_for_account' | 'keyword_search';
  /** Comment generation mode:
   * - 'ai': generate comment text via AI from a prompt
   * - 'fixed': cycle through a fixed list of comment templates */
  comment_mode: 'ai' | 'fixed';
  // ai mode
  prompt?: string;           // prompt describing what the AI should generate
  // fixed mode
  fixed_comments?: string[]; // list of comment templates to cycle through
  // urls mode
  urls?: string[];
  // seeding_for_account mode
  tiktok_ids?: string[];
  count?: number;
  order?: 'newest' | 'random' | 'top';
  // keyword_search mode
  keyword?: string;
}

// Create Post Instagram Config
export interface CreatePostInstagramConfig {
  content_type: 'image' | 'both';
  content_hints: string;
  image_source_type?: 'none' | 'context' | 'vault_asset' | 'character_context';
  image_asset_id?: string;
  brand_info_node_id?: string;
}

// Comment Post Instagram Config
export interface CommentPostInstagramConfig {
  urls: string[];
  generation_method?: 'ai' | 'fixed';
  fixed_comments?: string[];
  content_hints: string;
  brand_info_node_id?: string;
  comment_mode: 'discussion' | 'seeding';
  seeding_mode?: string;
}


// Like Post Instagram Config
export interface LikePostInstagramConfig {
  urls?: string[];
}

// Repost Post Instagram Config
export interface RepostPostInstagramConfig {
  urls?: string[];
}

// Follow Instagram Config
export interface FollowInstagramConfig {
  urls?: string[];
}

// Reply Comment Instagram Config
export interface ReplyCommentInstagramConfig {
  urls?: string[];
  keywords?: string[];
  reply_template?: string;
  max_comments?: number;
}

// Create Post Threads Config
export interface CreatePostThreadsConfig {
  content_type: 'text' | 'image' | 'both';
  content_hints: string;
  image_source_type?: 'none' | 'context' | 'vault_asset' | 'character_context';
  image_asset_id?: string;
  brand_info_node_id?: string;
}

// Comment Post Threads Config
export interface CommentPostThreadsConfig {
  urls: string[];
  generation_method?: 'ai' | 'fixed';
  fixed_comments?: string[];
  content_hints: string;
  brand_info_node_id?: string;
  comment_mode: 'discussion' | 'seeding';
  seeding_mode?: string;
}

// Like Post Threads Config
export interface LikePostThreadsConfig {
  urls?: string[];
}

// Share Post Threads Config
export interface SharePostThreadsConfig {
  urls?: string[];
  share_type?: 'repost' | 'quote';
}

// Follow Threads Config
export interface FollowThreadsConfig {
  urls?: string[];
}

// Reply Comment TikTok Config
export interface ReplyCommentTiktokConfig {
  /** Dispatch mode:
   * - 'urls': reply to comments on specific video URLs directly
   * - 'seeding_for_account': browse @tiktok_id profiles and reply on videos
   * - 'keyword_search': search a keyword, open videos, reply to comments */
  dispatch_mode: 'urls' | 'seeding_for_account' | 'keyword_search';
  /** Comment targeting mode:
   * - 'keyword': reply only to comments containing at least one keyword
   * - 'random': randomly pick comments to reply to */
  reply_mode: 'keyword' | 'random';
  keywords?: string[];       // keywords to match (reply_mode='keyword')
  max_replies?: number;      // max replies per video (default 5)
  /** Reply content mode:
   * - 'ai': AI generates reply from prompt
   * - 'fixed': cycle through fixed reply templates */
  comment_mode: 'ai' | 'fixed';
  // ai mode
  prompt?: string;
  // fixed mode
  fixed_comments?: string[];
  // urls mode
  urls?: string[];
  // seeding_for_account mode
  tiktok_ids?: string[];
  count?: number;
  order?: 'newest' | 'random' | 'top';
  // keyword_search mode
  keyword?: string;
}

// Like Comment TikTok Config
export interface LikeCommentTiktokConfig {
  /** Dispatch mode:
   * - 'urls': like comments on specific video URLs directly
   * - 'seeding_for_account': browse @tiktok_id profiles and like comments on videos
   * - 'keyword_search': search a keyword, open videos, like comments */
  dispatch_mode: 'urls' | 'seeding_for_account' | 'keyword_search';
  /** Comment targeting mode:
   * - 'keyword': like only comments containing at least one keyword
   * - 'random': randomly pick comments to like */
  target_mode: 'keyword' | 'random';
  keywords?: string[];   // keywords to match (target_mode='keyword')
  max_likes?: number;    // max likes per video (default 10)
  // urls mode
  urls?: string[];
  // seeding_for_account mode
  tiktok_ids?: string[];
  count?: number;
  order?: 'newest' | 'random' | 'top';
  // keyword_search mode
  keyword?: string;
}

// Like Post TikTok Config
export interface LikePostTiktokConfig {
  /** Dispatch mode:
   * - 'urls': like specific video URLs directly
   * - 'seeding_for_account': like videos from specified TikTok profiles
   * - 'keyword_search': like videos found via keyword search */
  dispatch_mode: 'urls' | 'seeding_for_account' | 'keyword_search';
  // urls mode
  urls?: string[];
  // seeding_for_account mode
  tiktok_ids?: string[];       // e.g. ["@username1", "@username2"]
  count?: number;              // number of videos to like per account/keyword
  order?: 'newest' | 'random' | 'top'; // newest/random for seeding_for_account; top/random for keyword_search
  // keyword_search mode
  keyword?: string;
}

// Like Posts Config
export interface LikePostsConfig {
  urls: string[]; // List of post URLs to like (one per line in UI) — used when dispatch_mode="specific"
  dispatch_mode?: 'specific' | 'managed' | 'group_posts' | 'filter_posts' | 'from_top_posts'; // Dispatch mode (default: "specific")
  seeding_contents_manager_node_id?: string; // Node ID of seeding_contents_manager (managed mode)
  find_group_posts_node_id?: string; // Node ID of find_group_posts (group_posts mode)
  filter_post_node_id?: string; // Node ID of filter_post (filter_posts mode)
  find_top_posts_node_id?: string; // Node ID of find_top_posts (from_top_posts mode)
}

// Interact Config - Interact with Facebook content
export interface InteractConfig {
  type: 'all' | 'newsfeed' | 'reels' | 'story'; // Interaction mode
  duration_minutes: number; // Duration of interaction
  max_likes: number; // Max likes
  max_shares: number; // Max shares
  max_comments: number; // Max comments
  keywords?: string[]; // Target keywords for algorithmic warming on Reels/Watch
}

// Check Message Config - Check unread messages (parallel scanner)
export interface CheckMessageConfig {
  // Empty - no config needed
}

// Share URL Config
export interface ShareURLConfig {
  url: string; // URL to share (post/profile/page) — used when dispatch_mode="specific"
  /**
   * Share mode:
   * - "with_caption" (default): AI generates a caption before sharing
   * - "silent": press Share button only, no caption, no AI call
   */
  share_mode?: 'with_caption' | 'silent';
  dispatch_mode?: 'specific' | 'managed' | 'group_posts' | 'filter_posts' | 'from_top_posts'; // Dispatch mode (default: "specific")
  seeding_contents_manager_node_id?: string; // Node ID of seeding_contents_manager (managed mode)
  find_group_posts_node_id?: string; // Node ID of find_group_posts (group_posts mode)
  filter_post_node_id?: string; // Node ID of filter_post (filter_posts mode)
  find_top_posts_node_id?: string; // Node ID of find_top_posts (from_top_posts mode)
}

// Filter Post Config - Advanced post extraction with 2 modes
export interface FilterPostConfig {
  source_type: 'keyword' | 'my_groups'; // Discovery mode
  search_mode?: 'keyword' | 'hashtag';  // 'keyword' = normal text, 'hashtag' = #tag format
  keyword?: string; // Search keyword / hashtag (e.g. "thời trang" or "#thời_trang")
  recent_only: boolean; // Filter for recent posts only (for keyword search)
  max_posts: number; // Maximum posts to collect (default: 15, max: 50)
  max_days: number; // Maximum age in days (default: 3)
}

export interface FindTrendFBConfig {
  // Keyword resolver shared by both modes.
  keyword?: string; // Optional: override keyword (empty -> auto from seeding_profile)

  // Discovery mode:
  // - personal: current hashtag feed discovery (legacy behavior)
  // - group: search groups by keyword, pick top groups, then pick representative post
  // - fanpage: search pages by keyword, pick top pages by followers, then pick representative post
  discovery_mode?: 'personal' | 'group' | 'fanpage';

  // Scroll depth used by discovery steps (2-15).
  max_scrolls: number;

  // Group mode options (all optional; worker applies safe defaults)
  group_search_url?: string; // Optional explicit search URL. Empty -> auto build /search/groups?q={keyword}
  group_count?: number;      // How many groups to deep-scan (default 5)
  posts_per_group?: number;  // How many newest posts to inspect per group (default 20)

  // Fanpage mode options (all optional; worker applies safe defaults)
  page_search_url?: string; // Optional explicit search URL. Empty -> auto build /search/pages?q={keyword}
  page_count?: number;      // How many top-followed pages to deep-scan (default 5)
  posts_per_page?: number;  // How many newest posts to inspect per page (default 20)

  // Existing optional advanced fields kept for backward compatibility.
  hashtags?: string;
  description?: string;
  require_media_for_generation?: boolean;
}

// Find Group Posts Config - Collect posts from specified Facebook groups filtered by date + criteria
export interface FindGroupPostsConfig {
  group_urls: string[];          // Facebook group URLs to scan (at least 1 required)
  criteria: string;              // Content filter input (behavior depends on search_mode)
  search_mode?: 'smart_ai' | 'keyword'; // smart_ai = semantic AI matching; keyword = strict keyword contains (accent/case-insensitive)
  days_back?: number;            // 0–60: include posts from today back N calendar days
  requirement: string;           // Label distinguishing result sets for multiple nodes with different criteria
  max_scrolls_per_group?: number; // Override scroll depth per group (0 = auto)
}

// Find Top Posts Config - Collect top posts by engagement from any FB URL (fanpage/group/profile)
export interface FindTopPostsConfig {
  source_url?: string;      // Legacy single URL (backward compatibility)
  source_urls?: string[];   // Multi-source URLs (fanpage/group/profile)
  days_back?: number;   // 1–30: scan posts from the last N days (default: 7)
  keyword?: string;         // Optional keyword filter — case-insensitive substring (no AI)
  top_n?: number;           // How many top posts to return (default: 1)
  max_scan?: number;        // Max posts to inspect before stopping (default/cap: 500)
  label?: string;           // Result-set label for file storage (default: "default")
}

// AI Generate Asset Config
// wkr-marketing acts as client to wkr-ai-controller:
// HTTPS POST /api/v1/worker/ai/request-worker → svc-business-marketing proxy
//   → svc-ai-controller POST /api/v1/tasks → wkr-ai-controller → Telegram vault
//
// Config: ONLY task_type (maps to a workflow in svc-ai-controller).
// Provider/model/goal/media_type are defined by the workflow — NOT configured here.
// All inputs (image, prompt) are received passively from the upstream node's output
// in the execution context.
export interface AIGenerateAssetConfig {
  /** Task type matching an active workflow in svc-ai-controller (e.g. "image.transform").
   *  Fetch available values from GET /api/v1/ai-workflows (proxied via /api/marketing/ai-workflows). */
  task_type: string;
}

export interface AIRemakeImageConfig {
  ai_provider?: 'grok' | 'gemini';
  model?: string;
}

export interface SendDirectMessageConfig {
  recipients: string[]; // Facebook UIDs, usernames, or profile URLs
  messages: string[];   // Message texts — all messages sent to every recipient
}

export interface PostAutoResponderConfig {
  post_url: string;    // URL of the Facebook post to scan (specific mode)
  keywords: string[];  // Keyword filter list (empty = use built-in defaults: inbox, ib, giá, etc.)
  message: string;     // Private DM content to send to each matching commenter
  reply_text: string;  // Public comment reply after sending DM
  // Dispatch mode: 'specific' (default) | 'managed' (SCM) | 'group_posts' (FGP) | 'filter_posts' | 'from_top_posts'
  dispatch_mode?: 'specific' | 'managed' | 'group_posts' | 'filter_posts' | 'from_top_posts';
  seeding_contents_manager_node_id?: string; // SCM node ID (managed mode)
  find_group_posts_node_id?: string;         // FGP node ID (group_posts mode)
  filter_post_node_id?: string;              // filter_post node ID (filter_posts mode)
  find_top_posts_node_id?: string;           // find_top_posts node ID (from_top_posts mode)
}

export interface GroupRandomMessengerConfig {
  group_url: string;  // Facebook group URL
  message: string;    // DM text to send
  limit: number;      // Max recipients per run (default 5)
}

// Post to Fanpage Config — posts to official Facebook Pages via Graph API (no Playwright)
export interface PostToFanpageConfig {
  /** MongoDB ObjectID hex of FanpagePlatform docs to target (empty = all connected pages) */
  page_ids?: string[];
  content_source_mode?: 'ai' | 'manual' | 'upstream';
  content_strategy?: 'shared' | 'per_page';
  content_type: 'text' | 'image' | 'both';
  manual_message?: string;
  /** Freeform hints that guide AI content generation */
  content_hints: string;
  brand_info_node_id?: string;
  /** Image source:
   *  - "none"        – no image attachment
   *  - "context"     – vault_asset_id from upstream ai_generate_asset node
   *  - "vault_asset" – static pick from media vault (requires image_asset_id) */
  image_source_type?: 'none' | 'context' | 'vault_asset' | 'character_context';
  image_asset_id?: string;
  link_url?: string;
  add_to_seeding_contents?: boolean;
  /** Folder IDs to register seeding content into; empty array = default folder. */
  seeding_folder_ids?: string[];
  /** Page scheduling strategy when multiple pages are targeted:
   *  - "all"         – post to every resolved page per run
   *  - "round_robin" – rotate through pages one-at-a-time across runs */
  schedule_mode?: 'all' | 'round_robin';
}

export interface FanpagePostTargetingConfig {
  page_ids?: string[];
  target_post_mode?: 'specific_posts' | 'latest_posts' | 'top_engagement';
  post_ids?: string[];
  post_url?: string;
  selected_page_id?: string;
  selected_post_id?: string;
  selected_post_url?: string;
  max_posts_to_scan?: number;
  feed_fetch_limit?: number;
  schedule_mode?: 'all' | 'round_robin';
}

export interface FanpageCommentOnPostConfig extends FanpagePostTargetingConfig {
  comment_mode?: 'discussion' | 'seeding';
  seeding_mode?: 'ask_info' | 'interest' | 'inbox_cta' | 'social_proof' | 'close' | '';
  content_hints: string;
  brand_info_node_id?: string;
  tag_names?: string[];
  hashtags?: string[];
}

export interface FanpageReplyToCommentsConfig extends FanpagePostTargetingConfig {
  max_replies?: number;
  max_comments?: number;
  max_replies_per_post?: number;
  max_comments_to_scan?: number;
  content_hints: string;
  comment_style?: Record<string, any>;
  brand_info_node_id?: string;
  reply_mode?: 'random' | 'keyword';
  reply_keywords?: string[];
  reply_max_per_keyword?: number;
}

export interface FanpageLikeCommentsConfig extends FanpagePostTargetingConfig {
  comment_filter_mode?: 'all' | 'keyword';
  comment_keywords?: string[];
  max_comments_to_like?: number;
}

export interface FanpageModerateCommentsConfig extends FanpagePostTargetingConfig {
  moderation_action?: 'hide' | 'unhide' | 'delete';
  comment_filter_mode?: 'all' | 'keyword';
  comment_keywords?: string[];
  max_comments_to_moderate?: number;
}

export interface FanpagePrivateReplyConfig extends FanpagePostTargetingConfig {
  message_source_mode?: 'ai' | 'manual';
  manual_message?: string;
  content_hints?: string;
  brand_info_node_id?: string;
  comment_filter_mode?: 'all' | 'keyword';
  comment_keywords?: string[];
  max_messages_per_post?: number;
  reply_max_per_keyword?: number;
}

export interface FacebookReadInsightConfig {
  page_ids?: string[];
}

export interface WorkflowNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: {
    label: string;
    config?: Record<string, any>; // Legacy generic config
    // canonical keys
    brand_info_config?: BrandInfoConfig;
    seeding_contents_manager_config?: SeedingContentsManagerConfig;
    send_direct_message_config?: SendDirectMessageConfig;
    post_auto_responder_config?: PostAutoResponderConfig;
    group_random_messenger_config?: GroupRandomMessengerConfig;
    post_to_fanpage_config?: PostToFanpageConfig;
    fanpage_comment_on_post_config?: FanpageCommentOnPostConfig;
    fanpage_reply_to_comments_config?: FanpageReplyToCommentsConfig;
    fanpage_like_comments_config?: FanpageLikeCommentsConfig;
    fanpage_moderate_comments_config?: FanpageModerateCommentsConfig;
    fanpage_private_reply_config?: FanpagePrivateReplyConfig;
    facebook_read_insight_config?: FacebookReadInsightConfig;
    account_selector_config?: AccountSelectorConfig;
    post_to_profile_config?: PostToProfileConfig;
    post_to_group_config?: PostToGroupConfig;
    comment_on_post_config?: CommentOnPostConfig;
    reply_to_comments_config?: ReplyToCommentsConfig;
    find_join_groups_config?: FindJoinGroupsConfig;
    join_groups_config?: JoinGroupsConfig;
    like_posts_config?: LikePostsConfig;
    comment_post_tiktok_config?: CommentPostTiktokConfig;
    reply_comment_tiktok_config?: ReplyCommentTiktokConfig;
    like_comment_tiktok_config?: LikeCommentTiktokConfig;
    like_post_tiktok_config?: LikePostTiktokConfig;
    share_post_tiktok_config?: SharePostTiktokConfig;
    create_post_instagram_config?: CreatePostInstagramConfig;
    like_post_instagram_config?: LikePostInstagramConfig;
    repost_post_instagram_config?: RepostPostInstagramConfig;
    comment_post_instagram_config?: CommentPostInstagramConfig;
    follow_instagram_config?: FollowInstagramConfig;
    reply_comment_instagram_config?: ReplyCommentInstagramConfig;
    create_post_threads_config?: CreatePostThreadsConfig;
    like_post_threads_config?: LikePostThreadsConfig;
    share_post_threads_config?: SharePostThreadsConfig;
    comment_post_threads_config?: CommentPostThreadsConfig;
    follow_threads_config?: FollowThreadsConfig;
    share_url_config?: ShareURLConfig;
    filter_post_config?: FilterPostConfig;
    find_trend_fb_config?: FindTrendFBConfig;
    find_group_posts_config?: FindGroupPostsConfig;
    find_top_posts_config?: FindTopPostsConfig;
    ai_generate_asset_config?: AIGenerateAssetConfig;
    ai_remake_image_config?: AIRemakeImageConfig;
    ai_grok_remake_image_config?: AIRemakeImageConfig;
    linkedin_connect_message_config?: LinkedinConnectMessageConfig;
    linkedin_join_group_config?: LinkedinJoinGroupConfig;
    find_trend_omnichannel_config?: FindTrendOmnichannelConfig;
    download_video_config?: DownloadVideoConfig;
    analyze_viral_content_config?: AnalyzeViralContentConfig;
    auto_repurpose_video_config?: AutoRepurposeVideoConfig;
    vault_upload_file_config?: VaultUploadFileConfig;
    // alternative keys used in tests/backend or older payloads
    account_selector?: AccountSelectorConfig;
    postToProfileConfig?: PostToProfileConfig;
    commentOnPostConfig?: CommentOnPostConfig;
    find_groups_config?: FindJoinGroupsConfig;
    find_groups?: FindJoinGroupsConfig;
    // allow arbitrary extra fields to avoid strict typing errors for legacy payloads
    [key: string]: any;
  };
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
}

export interface WorkflowConfig {
  run_type?: 'recurring' | 'one_shot'; // "recurring" = weekly schedule (default) | "one_shot" = run immediately once
  global_delay: {
    min_seconds: number;
    max_seconds: number;
  };
  // Global workflow execution settings
  execution_mode: 'sequential' | 'parallel';
  time_windows?: TimeWindow[];
  tasks_per_week: number;
  timezone: string; // IANA timezone (e.g., "Asia/Ho_Chi_Minh", "America/New_York")
}

export interface Workflow {
  _id?: string;
  campaign_id?: string;
  name: string;
  description: string;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  config: WorkflowConfig;
  status?: 'draft' | 'active' | 'inactive' | 'completed'; // 'completed' = one-shot finished
  activated_at?: string;
  deactivated_at?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
}

export interface NodeType {
  type: string;
  label: string;
  category: 'control' | 'action' | 'timing' | 'data' | 'with page' | 'with post' | 'with group' | 'with profile' | 'ai_flow_action' | 'messaging' | 'with tiktok' | 'with zalo' | 'with instagram' | 'with threads' | 'video_production';
  badgeVariant: 'success' | 'danger' | 'info' | 'warning';
  icon?: string;
  defaultConfig: Record<string, any>;
  shortDescription?: string;
  frequencyMultiplier?: number; // Default execution frequency multiplier (1.0 = base, 2.0 = 2x base)
}

// Master Timeline Configuration (set at account_selector level)
export interface MasterTimeline {
  activeHours: {
    start: string; // HH:MM format (e.g., "09:00") - stored as UTC in DB
    end: string;   // HH:MM format (e.g., "22:00") - stored as UTC in DB
  };
  timeWindows?: { start: string; end: string }[]; // Multiple active windows per day
  weekdays: number[]; // 0=Sunday, 1=Monday, ..., 6=Saturday
  baseFrequency: number; // Base executions per week (e.g., 3)
  timezone?: string;
}

export interface ZaloAddFriendConfig {
  phone_numbers: Array<{ phoneNumber: string; displayName?: string; message?: string }>;
  global_message: string;
  min_delay: number;
  max_delay: number;
}

export interface ZaloJoinGroupConfig {
  invite_link: string;
}

export interface ZaloGroupMembersConfig {
  group_id: string;
  group_name: string;
}

export interface ZaloProactiveSenderConfig {
  recipients: Array<{ userId?: string; phoneNumber?: string; displayName?: string; text_content?: string; image_paths?: string[] }>;
  global_text: string;
  global_images: string[];
  min_delay: number;
  max_delay: number;
}

export interface LinkedinConnectMessageConfig {
  target_identifier: string;
  message_content: string;
  action_type?: 'auto' | 'direct_message' | 'connect_note';
  max_limit?: number;
}

export interface LinkedinJoinGroupConfig {
  target_identifier: string;
  max_limit?: number;
  auto_join?: boolean;
}

export interface FindTrendOmnichannelConfig {
  platform: 'tiktok' | 'youtube_shorts' | 'douyin' | 'facebook' | 'instagram';
  keywords: string[];
  category?: string;
  max_results?: number;
  time_window_hours?: number;
  min_velocity_score?: number;
  auto_expand_hashtags?: boolean;
  early_viral_only?: boolean;
}

export interface DownloadVideoConfig {
  urls?: string[];
  output_dir?: string;
  max_duration?: number;
  resolution?: string;
}

export interface AnalyzeViralContentConfig {
  brand_name?: string;
  brand_tone?: string;
  target_audience?: string;
  target_language?: string;
  custom_prompt?: string;
}

export interface AutoRepurposeVideoConfig {
  file_path?: string;
  script?: string;
  voice?: string;
  target_aspect_ratio?: string;
  bg_music_volume?: number;
  burn_subtitles?: boolean;
  output_dir?: string;
  max_duration_sec?: number;
}

export interface VaultUploadFileConfig {
  file_path?: string;
  goal?: string;
  title?: string;
  description?: string;
}

export const NODE_TYPES: NodeType[] = [
  // ============ LINKEDIN ============
  {
    type: 'linkedin_connect_message',
    label: 'LinkedIn Connect & Msg',
    category: 'messaging',
    badgeVariant: 'warning',
    shortDescription: 'Tìm kiếm, kết nối hoặc gửi tin nhắn trực tiếp qua LinkedIn',
    defaultConfig: {
      target_identifier: '',
      message_content: '',
      action_type: 'auto',
      max_limit: 5,
    },
  },
  {
    type: 'linkedin_join_group',
    label: 'LinkedIn Join Groups',
    category: 'with group',
    badgeVariant: 'warning',
    shortDescription: 'Tìm kiếm, thu thập thông tin và tự động tham gia nhóm LinkedIn theo từ khóa',
    defaultConfig: {
      target_identifier: '',
      max_limit: 5,
      auto_join: true,
    },
  },
  // ============ BRAND & CAMPAIGN INFO ============
  {
    type: 'brand_info',
    label: 'Brand Info',
    category: 'data',
    badgeVariant: 'warning',
    shortDescription: 'Configure brand information and messaging guidelines for seeding campaigns',
    defaultConfig: {
      brand_name: '',
      industry: '',
      category: '',
      product_names: [],
      website: '',
      description: '',
      core_topics: [],
      target_demographics: {
        age_groups: [],
        locations: [],
        interests: [],
      },
      mention_style: 'organic',
      tone_of_voice: 'friendly',
    },
  },
  {
    type: 'seeding_contents_manager',
    label: 'Seeding Contents Manager',
    category: 'data',
    badgeVariant: 'warning',
    shortDescription: 'Manage seeding post URLs and per-account interaction frequency controls',
    defaultConfig: {
      name: '',
      seeding_accounts: [],
    },
  },

  // ============ DATA SELECTOR ============
  {
    type: 'account_selector',
    label: 'Select Accounts',
    category: 'data',
    badgeVariant: 'info',
    frequencyMultiplier: 1.0, // Base frequency
    shortDescription: 'Select and configure accounts for workflow execution',
    defaultConfig: {
      platform: 'facebook',
      selected_accounts: [],
      mode: 'specific',
      masterTimeline: {
        activeHours: {
          start: '09:00',
          end: '22:00'
        },
        timeWindows: [],
        weekdays: [1, 2, 3, 4, 5, 6, 0], // All days
        baseFrequency: 3, // 3 times per week
        timezone: 'Asia/Ho_Chi_Minh'
      }
    },
  },

  // ============ SEARCH & DISCOVERY ============
  // NOTE: search_groups removed from palette — superseded by join_groups (random mode)
  {
    type: 'filter_post',
    label: 'Filter Posts',
    category: 'with post',
    badgeVariant: 'info',
    frequencyMultiplier: 2.0,
    shortDescription: 'Collect post URLs by keyword or from joined groups for downstream nodes',
    defaultConfig: {
      source_type: 'keyword',
      search_mode: 'keyword',
      keyword: '',
      recent_only: true,
      max_posts: 15,
      max_days: 3,
    },
  },
  {
    type: 'find_trend_fb',
    label: 'Find Trending Posts (FB)',
    category: 'with post',
    badgeVariant: 'info',
    frequencyMultiplier: 1.5,
    shortDescription: 'Discover trending Facebook posts via demographic-driven keyword selection + Gemini virality analysis',
    defaultConfig: {
      keyword: '',
      discovery_mode: 'personal',
      max_scrolls: 5,
      group_search_url: '',
      group_count: 5,
      posts_per_group: 20,
      page_search_url: '',
      page_count: 5,
      posts_per_page: 20,
    },
  },
  {
    type: 'find_group_posts',
    label: 'Find Group Posts',
    category: 'with group',
    badgeVariant: 'info',
    frequencyMultiplier: 1.5,
    shortDescription: 'Collect posts from specified Facebook groups filtered by date and content criteria. Saves to JSON file with per-account dedup tracking.',
    defaultConfig: {
      group_urls: [],
      search_mode: 'smart_ai',
      criteria: '',
        days_back: 0,
      requirement: 'default',
    },
  },
  {
    type: 'find_top_posts',
    label: 'Find Top Posts',
    category: 'with group',
    badgeVariant: 'info',
    frequencyMultiplier: 1.5,
    shortDescription: 'Collect top-N posts by engagement from any Facebook URL (fanpage, group, or profile). Sorted by likes + comments + shares.',
    defaultConfig: {
      source_url: '',
      days_back: 7,
      keyword: '',
      top_n: 1,
      label: 'default',
    },
  },

  // ============ JOINING & FOLLOWING ============
  // NOTE: find_join_groups removed from palette — superseded by join_groups (random mode)
  {
    type: 'join_groups',
    label: 'Join Groups',
    category: 'with group',
    badgeVariant: 'info',
    frequencyMultiplier: 0.7, // 0.7x base (~2x/week if base=3)
    shortDescription: 'Join groups by keyword search (random) or from a list of URLs (by_url)',
    defaultConfig: {
      join_mode: 'random',
      max_joins: 3,
      keywords: [],
      group_urls: [],
    },
  },
  {
    type: 'like_pages',
    label: 'Like Pages',
    category: 'with page',
    badgeVariant: 'info',
    frequencyMultiplier: 0.7, // 0.7x base (~2x/week if base=3)
    shortDescription: 'Like pages with multi-strategy clicking (physical/force/JS)',
    defaultConfig: {
      page_urls: [],
      max_likes: 3,
      from_suggestions: false,
    },
  },

  // ============ CONTENT CREATION ============
  {
    type: 'post_to_profile',
    label: 'Post to Profile',
    category: 'with profile',
    badgeVariant: 'info',
    frequencyMultiplier: 1.0, // 1x base (3x/week if base=3)
    shortDescription: 'Create and post content to account profile',
    defaultConfig: {
      content_type: 'text',
      has_image: false,
      content_hints: '',
    },
  },
  {
    type: 'post_to_group',
    label: 'Post to Group',
    category: 'with group',
    badgeVariant: 'info',
    frequencyMultiplier: 0.7, // 0.7x base (~2x/week if base=3)
    shortDescription: 'Post content to specific groups or communities',
    defaultConfig: {
      group_urls: [],
      content_hints: '',
    },
  },
  {
    type: 'post_to_fanpage',
    label: 'Post to Fanpage',
    category: 'with page',
    badgeVariant: 'info',
    frequencyMultiplier: 1.0,
    shortDescription: 'Publish content to connected Facebook Pages via Graph API — no account login required',
    defaultConfig: {
      page_ids: [],
      content_source_mode: 'ai',
      content_strategy: 'shared',
      content_type: 'text',
      manual_message: '',
      content_hints: '',
      schedule_mode: 'all',
    },
  },
  {
    type: 'fanpage_comment_on_post',
    label: 'Fanpage Comment on Post',
    category: 'with page',
    badgeVariant: 'info',
    frequencyMultiplier: 1.3,
    shortDescription: 'Comment on recent posts of connected fanpages via Graph API',
    defaultConfig: {
      page_ids: [],
      target_post_mode: 'latest_posts',
      post_ids: [],
      selected_page_id: '',
      selected_post_id: '',
      selected_post_url: '',
      max_posts_to_scan: 1,
      feed_fetch_limit: 10,
      content_hints: '',
      comment_mode: 'discussion',
      schedule_mode: 'all',
    },
  },
  {
    type: 'fanpage_reply_to_comments',
    label: 'Fanpage Reply to Comments',
    category: 'with page',
    badgeVariant: 'info',
    frequencyMultiplier: 1.2,
    shortDescription: 'Reply to commenters on fanpage posts via Graph API',
    defaultConfig: {
      page_ids: [],
      target_post_mode: 'latest_posts',
      post_ids: [],
      selected_page_id: '',
      selected_post_id: '',
      selected_post_url: '',
      max_posts_to_scan: 5,
      max_replies: 2,
      max_comments: 20,
      feed_fetch_limit: 10,
      content_hints: '',
      reply_mode: 'random',
      reply_keywords: [],
      schedule_mode: 'all',
    },
  },
  {
    type: 'fanpage_like_comments',
    label: 'Fanpage Like Comments',
    category: 'with page',
    badgeVariant: 'info',
    frequencyMultiplier: 1.0,
    shortDescription: 'Like matching comments on selected fanpage posts via Graph API',
    defaultConfig: {
      page_ids: [],
      target_post_mode: 'latest_posts',
      post_ids: [],
      selected_page_id: '',
      selected_post_id: '',
      selected_post_url: '',
      max_posts_to_scan: 3,
      feed_fetch_limit: 10,
      comment_filter_mode: 'all',
      comment_keywords: [],
      max_comments_to_like: 5,
      schedule_mode: 'all',
    },
  },
  {
    type: 'fanpage_moderate_comments',
    label: 'Fanpage Moderate Comments',
    category: 'with page',
    badgeVariant: 'info',
    frequencyMultiplier: 1.0,
    shortDescription: 'Hide, unhide, or delete matching fanpage comments via Graph API',
    defaultConfig: {
      page_ids: [],
      target_post_mode: 'latest_posts',
      post_ids: [],
      selected_page_id: '',
      selected_post_id: '',
      selected_post_url: '',
      max_posts_to_scan: 3,
      feed_fetch_limit: 10,
      moderation_action: 'hide',
      comment_filter_mode: 'keyword',
      comment_keywords: [],
      max_comments_to_moderate: 5,
      schedule_mode: 'all',
    },
  },
  {
    type: 'fanpage_private_reply',
    label: 'Fanpage Private Reply',
    category: 'with page',
    badgeVariant: 'info',
    frequencyMultiplier: 1.0,
    shortDescription: 'Send private replies to commenters on fanpage posts via Graph API',
    defaultConfig: {
      page_ids: [],
      target_post_mode: 'latest_posts',
      post_ids: [],
      selected_page_id: '',
      selected_post_id: '',
      selected_post_url: '',
      max_posts_to_scan: 3,
      feed_fetch_limit: 10,
      message_source_mode: 'ai',
      manual_message: '',
      content_hints: '',
      comment_filter_mode: 'keyword',
      comment_keywords: [],
      max_messages_per_post: 3,
      reply_max_per_keyword: 1,
      schedule_mode: 'all',
    },
  },
  {
    type: 'facebook_read_insight',
    label: 'Fanpage Read Insights',
    category: 'with page',
    badgeVariant: 'info',
    frequencyMultiplier: 1.0,
    shortDescription: 'Fetch impressions and engagements for the last 30 days of selected fanpages',
    defaultConfig: {
      page_ids: [],
    },
  },
  // ============ ENGAGEMENT ============
  {
    type: 'comment_on_post',
    label: 'Comment on Post',
    category: 'with post',
    badgeVariant: 'info',
    frequencyMultiplier: 1.7, // 1.7x base (~5x/week if base=3)
    shortDescription: 'Comment on posts with AI-generated content',
    defaultConfig: {
      post_url: '',
      comment_style: {
        tone: 'friendly',
        length: 'medium',
        emoji_usage: 'moderate',
      },
      content_hints: '',
    },
  },
  {
    type: 'like_posts',
    label: 'Like Posts',
    category: 'with post',
    badgeVariant: 'info',
    frequencyMultiplier: 2.3, // 2.3x base (~7x/week if base=3)
    shortDescription: 'Like posts from newsfeed and search results',
    defaultConfig: {
      target: 'newsfeed',
      max_likes: 10,
    },
  },
  {
    type: 'reply_to_comments',
    label: 'Reply to Comments',
    category: 'with post',
    badgeVariant: 'info',
    frequencyMultiplier: 1.3, // 1.3x base (~4x/week if base=3)
    shortDescription: 'Reply to comments on posts with relevant responses',
    defaultConfig: {
      post_url: '',
      max_replies: 1,
      max_comments: 20,
      content_hints: '',
      comment_style: {
        tone: 'friendly',
        emoji_usage: 'none',
        length: 'short',
      },
    },
  },

  {
    type: 'share_url',
    label: 'Share URL',
    category: 'with profile',
    badgeVariant: 'info',
    frequencyMultiplier: 1.0,
    shortDescription: 'Share any URL to your Facebook timeline',
    defaultConfig: {
      url: '',
      share_mode: 'with_caption',
    },
  },
  {
    type: 'comment_post_tiktok',
    label: 'Comment on TikTok',
    category: 'with tiktok',
    badgeVariant: 'info',
    frequencyMultiplier: 2.0,
    shortDescription: 'Comment on TikTok videos by direct URL, by profile seeding, or by keyword search',
    defaultConfig: {
      dispatch_mode: 'urls',
      comment_mode: 'ai',
      prompt: '',
      fixed_comments: [],
      urls: [],
      tiktok_ids: [],
      count: 10,
      order: 'newest',
      keyword: '',
    },
  },
  {
    type: 'reply_comment_tiktok',
    label: 'Reply Comment on TikTok',
    category: 'with tiktok',
    badgeVariant: 'info',
    frequencyMultiplier: 2.0,
    shortDescription: 'Reply to comments on TikTok videos. Supports keyword matching, random mode, AI or fixed reply text.',
    defaultConfig: {
      dispatch_mode: 'urls',
      reply_mode: 'keyword',
      keywords: [],
      max_replies: 5,
      comment_mode: 'ai',
      prompt: '',
      fixed_comments: [],
      urls: [],
      tiktok_ids: [],
      count: 10,
      order: 'newest',
      keyword: '',
    },
  },
  {
    type: 'like_comment_tiktok',
    label: 'Like Comment on TikTok',
    category: 'with tiktok',
    badgeVariant: 'info',
    frequencyMultiplier: 2.0,
    shortDescription: 'Like comments on TikTok videos. Supports keyword matching or random targeting.',
    defaultConfig: {
      dispatch_mode: 'urls',
      target_mode: 'keyword',
      keywords: [],
      max_likes: 10,
      urls: [],
      tiktok_ids: [],
      count: 10,
      order: 'newest',
      keyword: '',
    },
  },
  {
    type: 'like_post_tiktok',
    label: 'Like Post on TikTok',
    category: 'with tiktok',
    badgeVariant: 'info',
    frequencyMultiplier: 2.0,
    shortDescription: 'Like TikTok videos by direct URL, by profile seeding, or by keyword search',
    defaultConfig: {
      dispatch_mode: 'urls',
      urls: [],
      tiktok_ids: [],
      count: 10,
      order: 'newest',
      keyword: '',
    },
  },
  {
    type: 'share_post_tiktok',
    label: 'Share Post on TikTok',
    category: 'with tiktok',
    badgeVariant: 'info',
    frequencyMultiplier: 2.0,
    shortDescription: 'Repost (Đăng lại) TikTok videos by direct URL, by profile seeding, or by keyword search',
    defaultConfig: {
      dispatch_mode: 'urls',
      urls: [],
      tiktok_ids: [],
      count: 10,
      order: 'newest',
      keyword: '',
    },
  },
  {
    type: 'create_post_instagram',
    label: 'Create Post on Instagram',
    category: 'with instagram',
    badgeVariant: 'warning',
    frequencyMultiplier: 1.0,
    shortDescription: 'Create and post content (image + text) to Instagram profile',
    defaultConfig: {
      content_type: 'both',
      content_hints: '',
      image_source_type: 'none',
    },
  },
  {
    type: 'like_post_instagram',
    label: 'Like Post on Instagram',
    category: 'with instagram',
    badgeVariant: 'warning',
    frequencyMultiplier: 1.5,
    shortDescription: 'Like Instagram posts by direct URL.',
    defaultConfig: {
      urls: [],
    },
  },
  {
    type: 'repost_post_instagram',
    label: 'Repost Post on Instagram',
    category: 'with instagram',
    badgeVariant: 'warning',
    frequencyMultiplier: 1.5,
    shortDescription: 'Repost (Đăng lại) Instagram posts by direct URL.',
    defaultConfig: {
      urls: [],
    },
  },
  {
    type: 'comment_post_instagram',
    label: 'Comment Post on Instagram',
    category: 'with instagram',
    badgeVariant: 'warning',
    frequencyMultiplier: 1.5,
    shortDescription: 'Comment on Instagram posts using AI by direct URL.',
    defaultConfig: {
      urls: [],
      content_hints: '',
      comment_mode: 'discussion',
    },
  },
  {
    type: 'follow_instagram',
    label: 'Follow on Instagram',
    category: 'with instagram',
    badgeVariant: 'warning',
    frequencyMultiplier: 1.5,
    shortDescription: 'Follow Instagram profiles by direct URL.',
    defaultConfig: {
      urls: [],
    },
  },
  {
    type: 'reply_comment_instagram',
    label: 'Reply Comment on Instagram',
    category: 'with instagram',
    badgeVariant: 'warning',
    frequencyMultiplier: 1.5,
    shortDescription: 'Scan comments on Instagram posts, match keywords, and reply.',
    defaultConfig: {
      urls: [],
      keywords: [],
      reply_template: '',
      max_comments: 100,
    },
  },
  {
    type: 'create_post_threads',
    label: 'Create Post on Threads',
    category: 'with threads',
    badgeVariant: 'warning',
    frequencyMultiplier: 1.0,
    shortDescription: 'Create and post content (image + text) to Threads profile',
    defaultConfig: {
      content_type: 'both',
      content_hints: '',
      image_source_type: 'none',
    },
  },
  {
    type: 'like_post_threads',
    label: 'Like Post on Threads',
    category: 'with threads',
    badgeVariant: 'warning',
    frequencyMultiplier: 1.5,
    shortDescription: 'Like Threads posts by direct URL.',
    defaultConfig: {
      urls: [],
    },
  },
  {
    type: 'share_post_threads',
    label: 'Share Post on Threads',
    category: 'with threads',
    badgeVariant: 'warning',
    frequencyMultiplier: 1.5,
    shortDescription: 'Repost (Đăng lại) or Quote Threads posts by direct URL.',
    defaultConfig: {
      urls: [],
      share_type: 'repost',
    },
  },
  {
    type: 'comment_post_threads',
    label: 'Comment Post on Threads',
    category: 'with threads',
    badgeVariant: 'warning',
    frequencyMultiplier: 1.5,
    shortDescription: 'Comment on Threads posts by direct URL.',
    defaultConfig: {
      urls: [],
      content_hints: '',
      comment_mode: 'discussion',
    },
  },
  {
    type: 'follow_threads',
    label: 'Follow on Threads',
    category: 'with threads',
    badgeVariant: 'warning',
    frequencyMultiplier: 1.5,
    shortDescription: 'Follow Threads profiles by direct URL.',
    defaultConfig: {
      urls: [],
    },
  },
  {
    type: 'interact',
    label: 'Interact (Newsfeed/Reels)',
    category: 'with profile',
    badgeVariant: 'info',
    frequencyMultiplier: 2.3,
    shortDescription: 'Naturally interact with Facebook newsfeed, reels and stories',
    defaultConfig: {
      type: 'all',
      duration_minutes: 5,
      max_likes: 10,
      max_shares: 2,
      max_comments: 3,
    },
  },
  {
    type: 'check_message',
    label: 'Check Messages',
    category: 'with profile',
    badgeVariant: 'info',
    frequencyMultiplier: 2.0,
    shortDescription: 'Scan inbox for unread messages (parallel inbox scanner)',
    defaultConfig: {},
  },
  {
    type: 'send_direct_message',
    label: 'Send Direct Message',
    category: 'messaging',
    badgeVariant: 'warning',
    frequencyMultiplier: 0.5,
    shortDescription: 'Send Facebook direct messages to a list of recipients via their profile page. All messages sent to every recipient.',
    defaultConfig: {
      recipients: [],
      messages: [],
    },
  },
  {
    type: 'post_auto_responder',
    label: 'Post Auto Responder',
    category: 'messaging',
    badgeVariant: 'warning',
    frequencyMultiplier: 0.3,
    shortDescription: 'Scan a Facebook post for comments matching keywords (inbox, ib, giá…), send DMs and reply to those commenters automatically.',
    defaultConfig: {
      post_url: '',
      keywords: [],
      message: '',
      reply_text: 'Mình đã inbox bạn nhé!',
    },
  },
  {
    type: 'group_random_messenger',
    label: 'Group Random Messenger',
    category: 'messaging',
    badgeVariant: 'warning',
    frequencyMultiplier: 0.3,
    shortDescription: 'Scrape members from a Facebook group and send DMs to a random selection.',
    defaultConfig: {
      group_url: '',
      message: '',
      limit: 5,
    },
  },

  // ============ ACCOUNT CARE ============
  {
    type: 'view_notifications',
    label: 'View Notifications',
    category: 'with profile',
    badgeVariant: 'info',
    frequencyMultiplier: 2.3, // 2.3x base (~7x/week if base=3)
    shortDescription: 'Check and view account notifications regularly',
    defaultConfig: {
      check_all: true,
      interact: false,
    },
  },
  {
    type: 'accept_friend_requests',
    label: 'Accept Friend Requests (Passive)',
    category: 'with profile',
    badgeVariant: 'info',
    frequencyMultiplier: 1.0, // 1.0x base (~3x/week if base=3)
    shortDescription: 'Accept incoming friend requests with realistic human behavior',
    defaultConfig: {
      max_accepts: 10,
    },
  },
  {
    type: 'search_add_friend',
    label: 'Search & Add Friend (Active)',
    category: 'with profile',
    badgeVariant: 'info',
    frequencyMultiplier: 0.7, // 0.7x base (~2x/week if base=3) - more conservative for active requests
    shortDescription: 'Search users by keyword and send friend requests actively',
    defaultConfig: {
      keywords: [],
      max_add: 3,
    },
  },

  // ============ AI PROCESSING ============
  {
    type: 'ai_generate_asset',
    label: 'AI Generate Asset',
    category: 'ai_flow_action',
    badgeVariant: 'success',
    frequencyMultiplier: 1.0,
    shortDescription: 'Generic: use wkr-ai-controller to generate or transform an image/text asset (user selects task_type)',
    defaultConfig: {
      task_type: 'image.transform',
    },
  },
  {
    type: 'ai_remake_image',
    label: 'AI · Remake Image',
    category: 'ai_flow_action',
    badgeVariant: 'success',
    frequencyMultiplier: 1.0,
    shortDescription: 'Remake trend image with selectable provider (Grok/Gemini). Requires find_trend_fb upstream.',
    defaultConfig: {
      ai_provider: 'grok',
      model: '',
    },
  },
  {
    type: 'ai_text_process',
    label: 'AI Text Process',
    category: 'ai_flow_action',
    badgeVariant: 'success',
    frequencyMultiplier: 1.0,
    shortDescription: 'Transform text via wkr-ai-controller (rephrase / translate / shorten). Reads shared[remake_caption], writes shared[post_text].',
    defaultConfig: {
      input_source: 'auto',
      output_shared_key: 'post_text',
      task_type: 'text.generate',
    },
  },
  
  // ============ WITH ZALO ============
  {
    type: 'zalo_add_friend',
    label: 'Zalo Add Friend',
    category: 'with zalo',
    badgeVariant: 'info',
    frequencyMultiplier: 1.0,
    shortDescription: 'Automatically send friend requests to a list of Zalo phone numbers',
    defaultConfig: {
      phone_numbers: [],
      global_message: 'Xin chào, mình kết bạn nhé!',
      min_delay: 5,
      max_delay: 15,
    },
  },
  {
    type: 'zalo_join_group',
    label: 'Zalo Join Group',
    category: 'with zalo',
    badgeVariant: 'info',
    frequencyMultiplier: 1.0,
    shortDescription: 'Join Zalo group via invite link',
    defaultConfig: {
      invite_link: '',
    },
  },
  {
    type: 'zalo_group_members',
    label: 'Zalo Group Members',
    category: 'with zalo',
    badgeVariant: 'info',
    frequencyMultiplier: 1.0,
    shortDescription: 'Extract members from a Zalo group',
    defaultConfig: {
      group_id: '',
      group_name: '',
    },
  },
  {
    type: 'zalo_proactive_sender',
    label: 'Zalo Proactive Sender',
    category: 'with zalo',
    badgeVariant: 'info',
    frequencyMultiplier: 1.0,
    shortDescription: 'Send personalized messages to Zalo recipients',
    defaultConfig: {
      recipients: [],
      global_text: '',
      global_images: [],
      min_delay: 8,
      max_delay: 20,
    },
  },

  // ============ VIDEO PRODUCTION & TRENDS ============
  {
    type: 'find_trend_omnichannel',
    label: 'Find Trend Omnichannel',
    category: 'video_production',
    badgeVariant: 'warning',
    frequencyMultiplier: 1.5,
    shortDescription: 'Discover viral trends across TikTok, YouTube Shorts, Douyin, Facebook, Instagram',
    defaultConfig: {
      platform: 'tiktok',
      keywords: [],
      category: 'entertainment',
      max_results: 10,
      time_window_hours: 72,
      min_velocity_score: 50.0,
    },
  },
  {
    type: 'download_video',
    label: 'Download Video',
    category: 'video_production',
    badgeVariant: 'info',
    frequencyMultiplier: 1.0,
    shortDescription: 'Download source videos using yt-dlp metadata and stream extraction',
    defaultConfig: {
      urls: [],
      output_dir: '',
      max_duration: 180,
      resolution: '1080p',
    },
  },
  {
    type: 'analyze_viral_content',
    label: 'Analyze Viral Content',
    category: 'video_production',
    badgeVariant: 'success',
    frequencyMultiplier: 1.0,
    shortDescription: 'Deconstruct viral hook, retention tactics, and generate adapted brand scripts via AI',
    defaultConfig: {
      brand_name: '',
      brand_tone: 'friendly',
      target_audience: 'mass',
      target_language: 'Tiếng Việt',
      custom_prompt: '',
    },
  },
  {
    type: 'auto_repurpose_video',
    label: 'Auto Repurpose Video',
    category: 'video_production',
    badgeVariant: 'success',
    frequencyMultiplier: 1.0,
    shortDescription: 'Automated video production with Edge-TTS voiceover, kinetic subtitles, 9:16 vertical formatting, and FFmpeg ducking',
    defaultConfig: {
      file_path: '',
      script: '',
      voice: 'vi-VN-HoaiMyNeural',
      target_aspect_ratio: '9:16',
      bg_music_volume: 0.15,
      burn_subtitles: true,
      output_dir: '',
      max_duration_sec: 90,
    },
  },
  {
    type: 'vault_upload_file',
    label: 'Vault Upload File',
    category: 'video_production',
    badgeVariant: 'success',
    frequencyMultiplier: 1.0,
    shortDescription: 'Upload finished video or media file to Telegram Vault and index in MongoDB',
    defaultConfig: {
      file_path: '',
      goal: 'social',
      title: '',
      description: '',
    },
  }
];
